import type { Accessor, JSX } from "solid-js";
import { createContext, createMemo, For, Show } from "solid-js";
import { Dialog } from "@ark-ui/solid";
import Portal from "../../utils/Portal";
import { initializedContext } from "../../utils/initializedUtil";
import { createStore } from "solid-js/store";
import { isNil } from "lodash-es";
import { useBeforeLeave } from "@solidjs/router";
import uuid from "../../utils/uuid.ts";

type CloseBehaviour = "default" | "actions_only" | "off";
export type ModalConfig = {
  onClose?: () => void;
  content: () => JSX.Element;
  closeBehaviour?: CloseBehaviour;
  // Keeps the parent visible, including the backdrop (default: `false`). Useful for visibly stacking modals
  keepParent?: boolean;
  // Immediately close the modal without any animation. Useful for when the end of the animation would otherwise change focus again
  skipClosingAnimation?: boolean;
};

type ModalManagerContextValue = {
  open: (config: ModalConfig) => void;
  replace: (config: ModalConfig) => void;
  hasModalOpen: () => boolean;
};

type ModalContextValue = {
  position: Accessor<number>;
  canClose: Accessor<boolean>;
  close: () => void;
  changeCloseBehaviour: (value: CloseBehaviour) => void;
  setBlockClose: (blocked: boolean) => void;
  setOnCloseBlocked: (onCloseBlocked: (() => void) | undefined) => void;
  isClosing: Accessor<boolean>;
};

const ModalManagerContext = createContext<ModalManagerContextValue>();
const ModalContext = createContext<ModalContextValue>();

export const useModalManager = () => initializedContext(ModalManagerContext, "useModalManager", "ModalManagerProvider");
export const useCurrentModal = () => initializedContext(ModalContext, "useCurrentModal", "ModalContent");

export const MODAL_CLOSING_ANIMATION_DURATION = 200;

const createModal = (config: ModalConfig) => ({
  id: uuid(),
  closeBehaviour: "default" as const,
  isClosing: false,
  blocked: false,
  onCloseBlocked: undefined as (() => void) | undefined,
  ...config,
});

export const ModalManagerProvider = (props: { children?: JSX.Element }) => {
  const [stack, setStack] = createStore<
    (ModalConfig & { id: string; isClosing: boolean; blocked: boolean; onCloseBlocked?: () => void })[]
  >([]);

  const hasModals = () => {
    const lastIndex = stack.length - 1;
    if (lastIndex < 0) {
      return false;
    }

    return !stack[lastIndex].isClosing;
  };

  const open = (config: ModalConfig) => {
    setStack((prev) => [
      ...prev.filter((m) => !m.isClosing), // Ditch 'isClosing' modal's
      createModal(config),
    ]);
  };

  const replace = (config: ModalConfig) => {
    setStack([stack.length - 1], createModal(config));
  };

  const close = (index?: number) => {
    const indexToClose = index ?? stack.length - 1;
    const modal = stack.at(indexToClose);
    if (isNil(modal) || modal.isClosing) {
      return;
    }

    const isLastModal = indexToClose === 0;
    modal.onClose?.();

    if (isLastModal && !(modal.skipClosingAnimation ?? false)) {
      setStack([indexToClose], "isClosing", true);
      setTimeout(() => setStack((prev) => prev.filter(({ id }) => modal.id !== id)), MODAL_CLOSING_ANIMATION_DURATION);
    } else {
      setStack((prev) => prev.slice(0, -1));
    }
  };

  // Remove all open modals when navigating to another page
  useBeforeLeave(() => {
    setStack([]);
  });

  return (
    <ModalManagerContext.Provider value={{ open, replace, hasModalOpen: hasModals }}>
      {props.children}

      <For each={stack}>
        {(modal, index) => {
          const isBottomModal = createMemo(() => index() === 0);
          const isClosing = createMemo(() => modal.isClosing);
          const canClose = createMemo(() => modal.closeBehaviour !== "off");

          return (
            <Dialog.Root
              open={!isClosing()}
              onOpenChange={({ open }) => {
                if (open || !canClose()) {
                  return;
                }

                if (modal.blocked) {
                  modal.onCloseBlocked?.();
                } else {
                  close();
                }
              }}
              closeOnEscape={modal.closeBehaviour === "default"}
              closeOnInteractOutside={modal.closeBehaviour === "default"}
            >
              <Portal>
                <Show when={isBottomModal() || modal.keepParent === true}>
                  <Dialog.Backdrop class="fixed inset-0 z-10 block! size-full animate-backdrop-fade-out bg-overlay-default open:animate-backdrop-fade-in" />
                </Show>
                <Dialog.Positioner class="fixed inset-0 z-10 flex size-full items-center justify-center rounded-lg">
                  <Dialog.Content
                    class="group relative rounded-lg bg-surface-default"
                    classList={{
                      "animate-content-out open:animate-content-in": isBottomModal(),
                    }}
                  >
                    <ModalContext.Provider
                      value={{
                        position: index,
                        canClose,
                        close: () => close(index()),
                        changeCloseBehaviour: (value: CloseBehaviour) => {
                          setStack([index()], "closeBehaviour", value);
                        },
                        setBlockClose: (blocked: boolean) => {
                          setStack([index()], "blocked", blocked);
                        },
                        setOnCloseBlocked: (onCloseBlocked: (() => void) | undefined) => {
                          setStack([index()], "onCloseBlocked", () => onCloseBlocked);
                        },
                        isClosing,
                      }}
                    >
                      <div
                        classList={{ "group-data-has-nested:hidden": !(stack.at(index() + 1)?.keepParent ?? false) }}
                      >
                        {modal.content()}
                      </div>
                    </ModalContext.Provider>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </Dialog.Root>
          );
        }}
      </For>
    </ModalManagerContext.Provider>
  );
};
